import { APP_PATHS } from '@/config/routes/app-paths';
import { isGlobalAdmin } from '@/utils/is-global-admin';
import { getPathname } from '@/utils/validate-path';
import type { Admin } from '@/types/auth';
import type { ResolvedNotification } from '@/types/notifications';

/**
 * Whether a notification announces an Admin Settings change.
 *
 * The API does not expose the notification's own type, so the destination it carries is the
 * signal: every platform-configuration write (country, service, feature and pricing admin,
 * catalog visibility, roles) links to `/admin-settings`. Query strings and hashes are stripped
 * first so a deep link such as `/admin-settings/service-admin` still matches.
 */
export const isAdminSettingsNotification = (notification: ResolvedNotification): boolean => {
  if (notification.actionPath == null) {
    return false;
  }

  const pathname = getPathname(notification.actionPath);

  return pathname === APP_PATHS.adminSettings || pathname.startsWith(`${APP_PATHS.adminSettings}/`);
};

/**
 * The notifications a user is allowed to see.
 *
 * Admin Settings is Global-Admin-only, so its activity must not reach the bell or the Recent
 * activity table of anyone else — a workspace Admin has no way to open the page the notification
 * is about, and the change is not theirs. Everything else passes through untouched.
 *
 * Client-side gating is UX-only; the server decides what it hands out.
 */
export const visibleNotifications = (
  notifications: ResolvedNotification[],
  user: Admin | null,
  selectedWholesalerId: string | null,
): ResolvedNotification[] => {
  if (isGlobalAdmin(user, selectedWholesalerId)) {
    return notifications;
  }

  return notifications.filter((notification) => !isAdminSettingsNotification(notification));
};
